import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import "./autoFix.css";

const API = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";

interface LocationState {
  draftId: number;
  imageUrls: string[];
  riskScore: number;
}

export default function AutoFixPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const [fixedCaption, setFixedCaption] = useState<string | null>(null);
  const [fixedImages, setFixedImages] = useState<string[]>([]);
  const [imageIssues, setImageIssues] = useState<any[]>([]);
  const [imagesFixed, setImagesFixed] = useState<number>(0);
  const [newRiskScore, setNewRiskScore] = useState<number | null>(null);

  const token = localStorage.getItem("token");

  useEffect(() => {
    if (!state?.draftId) {
      navigate("/create");
    }
  }, [state, navigate]);

  const runAutoFix = async () => {
    if (!state?.draftId) return;

    // Product behaviour: only run if risk is high
    if (state.riskScore <= 40) {
      setInfo("Content is already safe. Auto-fix is not required.");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setInfo(null);

      const res = await axios.post(
        `${API}/ai/auto-fix`,
        {
          draft_id: state.draftId,
          image_urls: state.imageUrls || [],
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = res.data || {};

      // Backend may say skipped
      if (data.skipped) {
        setInfo(
          typeof data.message === "string"
            ? data.message
            : "No fix required. Content is safe."
        );
        setNewRiskScore(
          typeof data.risk_score === "number" ? data.risk_score : null
        );
        return;
      }

      setFixedCaption(
        typeof data.fixed_caption === "string" ? data.fixed_caption : null
      );

      setFixedImages(Array.isArray(data.fixed_images) ? data.fixed_images : []);

      setImageIssues(Array.isArray(data.image_issues) ? data.image_issues : []);

      setImagesFixed(
        typeof data.images_fixed === "number" ? data.images_fixed : 0
      );

      setNewRiskScore(
        typeof data.new_risk_score === "number" ? data.new_risk_score : null
      );
      // AUTO NAVIGATE TO PUBLISH PREVIEW AFTER SUCCESS
      navigate("/publish-preview", {
        state: {
          draftId: state.draftId,
          caption: data.fixed_caption || "",
          image: Array.isArray(data.fixed_images) && data.fixed_images.length > 0 ? data.fixed_images[0] : state.imageUrls?.[0] || "",
          allImages: data.fixed_images || [],
          originalImages: state.imageUrls || [],
          oldRisk: state.riskScore,
          newRisk: data.new_risk_score,
        },
      });

    } catch (e: any) {
      console.error("AUTO FIX ERROR:", e);

      const detail = e?.response?.data?.detail;

      if (typeof detail === "string") {
        setError(detail);
      } else if (detail?.msg) {
        setError(detail.msg);
      } else {
        setError("Auto-fix failed. Please try again."); 
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      className="autofix-root"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.7 }}
    >
      {/* BACK GLOW */}
      <div className="autofix-glow" />

      <div className="autofix-shell">
        {/* LEFT PANEL */}
        <div className="autofix-left">
          <motion.h1
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            Auto Fix Engine
          </motion.h1>

          <motion.p
            className="autofix-sub"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.35 }}
          >
            AI will repair unsafe text, blurry images, low-light photos and policy
            violations. You can download the fixed versions.
          </motion.p>

          {/* RISK INFO */}
          {typeof state?.riskScore === "number" && (
            <div className="risk-pill">
              Current Risk Score: <strong>{state.riskScore}</strong>
            </div>
          )}

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.96 }}
            className="autofix-run"
            onClick={runAutoFix}
            disabled={loading}
          >
            {loading ? "Analyzing & Fixing…" : "Run Auto Fix"}
          </motion.button>

          {info && <div className="autofix-info">{info}</div>}

          {error && (
            <div className="autofix-error">
              {typeof error === "string" ? error : "Something went wrong"}
            </div>
          )}

          {/* METRICS */}
          <AnimatePresence>
            {newRiskScore !== null && (
              <motion.div
                className="autofix-metrics"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
              >
                <div className="metric-card">
                  <span>Images Fixed</span>
                  <strong>{imagesFixed}</strong>
                </div>

                <div className="metric-card">
                  <span>New Risk Score</span>
                  <strong>{newRiskScore}</strong>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* RIGHT PANEL */}
        <div className="autofix-right">
          {/* FIXED CAPTION */}
          <AnimatePresence>
            {fixedCaption ? (
              <motion.div
                className="autofix-result-card"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
              >
                <h3>Optimized Caption</h3>
                <p>{fixedCaption}</p>
              </motion.div>
            ) : (
              <motion.div
                className="autofix-placeholder"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <p>AI fixed caption will appear here</p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* FIXED IMAGES */}
          <AnimatePresence>
            {fixedImages.length > 0 && (
              <motion.div
                className="autofix-images"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <h4>Fixed Images (Download)</h4>

                <div className="fixed-image-grid">
                  {fixedImages.map((url: string, idx: number) => (
                    <motion.div
                      key={idx}
                      className="fixed-image-box"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: idx * 0.05 }}
                    >
                      <img src={url} alt={`fixed-${idx}`} />
                      <a
                        href={url}
                        download
                        target="_blank"
                        rel="noopener noreferrer"
                        className="download-btn"
                      >
                        Download
                      </a>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* IMAGE ISSUES */}
          {Array.isArray(imageIssues) && imageIssues.length > 0 && (
            <motion.div
              className="autofix-issues"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <h4>Image Analysis</h4>

              {imageIssues.map((issue: any, idx: number) => (
                <div key={idx} className="issue-row">
                  <span>Image {idx + 1}</span>

                  <span className="issue-flags">
                    {issue?.blurry && <span className="flag bad">Blurry</span>}
                    {issue?.low_light && (
                      <span className="flag bad">Low Light</span>
                    )}
                    {issue?.sexual && (
                      <span className="flag danger">Sexual</span>
                    )}
                    {issue?.violent && (
                      <span className="flag danger">Violent</span>
                    )}

                    {!issue?.blurry &&
                      !issue?.low_light &&
                      !issue?.sexual &&
                      !issue?.violent && (
                        <span className="flag good">Looks Good</span>
                      )}
                  </span>
                </div>
              ))}
            </motion.div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
